"use client";
import { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useWebSiteContext } from "@core/helpers/client/contexts/WebSiteContext";
import Button from "@/components/ui/button";
import ModalComponent from "@/components/ui/ModalComponent";

export default function Variants({
  type,
  selected_labels,
  title,
  attributes,
  onClick,
  variant_id,
  isSize,
  sizeHintDescription,
}) {
  const [isSizeModalOpen, setIsSizeModalOpen] = useState(false);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);

  const activeLabel = attributes?.find(
    (label) => selected_labels?.[variant_id]?.split("-")?.[1] === label.id
  );

  const {
    web_site_setting: { web_site_storage_prefix_url },
  } = useWebSiteContext();

  // در موبایل هیچ محدودیتی روی تعداد آیتم‌های اسلایدر نداریم؛ همه‌ی تنوع‌ها
  // در اسلایدر افقی نمایش داده می‌شوند و مودال هم همه را نشان می‌دهد.
  const hasOptionsModal = attributes?.length >= 4;

  const renderAttributeItem = (label) => {
    const { id, value, title: labelTitle } = label;
    const active = selected_labels?.[variant_id]?.split("-")?.[1] === id;
    const disabled = label?.disabled;

    const handleClick = () => {
      onClick(label);
      if (isOptionsModalOpen) setIsOptionsModalOpen(false);
    };

    // گزینه‌ی ناموجود هنوز قابل انتخاب است (فرم «موجود شد خبرم کن» را نشان می‌دهد)، پس disabled واقعی نیست.
    // متنش neutral-400 روی neutral-100 بود (کنتراست ۲٫۲۷، Lighthouse: color-contrast)؛ neutral-600 (۷٫۰۵) +
    // خط‌خوردگی تا «ناموجود» بودنش هنوز دیده شود.
    const text_class = `text-sm ${disabled ? "text-jmt-neutral-600 line-through" : "text-jmt-main"}`;

    // `li` با onClick نه با کیبورد در دسترس بود و نه برای screen reader قابل انتخاب؛ دکمه با aria-pressed
    return (
      <li key={id} className="shrink-0 flex">
        <button
          type="button"
          onClick={handleClick}
          aria-pressed={!!active}
          aria-label={disabled ? `${labelTitle} (ناموجود)` : undefined}
          className={`cursor-pointer shrink-0 flex items-center justify-center rounded-md bg-jmt-white border border-jmt-neutral-200 whitespace-nowrap transition
        ${type === 2 ? "p-2" : "p-2"}
        ${disabled ? "border-dashed bg-jmt-neutral-100! cursor-not-allowed" : ""}
        ${active && !disabled ? "ring-2! ring-jmt-main! bg-jmt-white!" : ""}
   `} >

        {type === 2 ? (
          <div className="flex items-center gap-2 w-full">
            <span
              aria-hidden="true"
              className={`w-5 h-5 rounded-full border border-jmt-neutral-200 shrink-0`}
              style={{
                backgroundColor:
                  !value?.has_cover && value?.color ? value.color : undefined,
                backgroundImage:
                  value?.has_cover && value?.cover?.url
                    ? `url(${web_site_storage_prefix_url}/${value.cover.url})`
                    : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: disabled ? "grayscale(80%)" : "none",
              }}
            />
            <span className={text_class}> {labelTitle} </span>
          </div>
        ) : (
          <span className={text_class}> {labelTitle} </span>
        )}

        </button>
      </li>
    );
  };

  return (
    <div className="lg:space-y-3">

      {/* title */}
      <div className="flex items-center justify-between">

        <div className="flex items-center gap-0.5">
          <span className="text-jmt-main font-bold"> {title}: </span>

          {activeLabel && (
            <div className="flex items-center gap-1">
              <span className="text-jmt-main font-bold">{activeLabel.title}</span>

              {type === 2 && (
                <span
                  className="w-4 h-4 rounded-full border border-jmt-neutral-200 inline-block"
                  style={{
                    backgroundColor:
                      !activeLabel?.value?.has_cover && activeLabel?.value?.color
                        ? activeLabel.value.color
                        : undefined,
                    backgroundImage:
                      activeLabel?.value?.has_cover && activeLabel?.value?.cover?.url
                        ? `url(${web_site_storage_prefix_url}/${activeLabel.value.cover.url})`
                        : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex lg:hidden">
          {hasOptionsModal ?
            <Button
              onClick={() => setIsOptionsModalOpen(true)}
              text={<span>  مشاهده {attributes.length} {title} </span>}
              end_icon={<ChevronLeft className="w-[18px] h-[18px] lg:w-5 lg:h-5" />}
              color_type="tertiary"
              className="whitespace-nowrap"
            />
            : ""}
        </div>

        {isSize ?
          <Button
            onClick={() => setIsSizeModalOpen(true)}
            text={<span> راهنمای اندازه  </span>}
            end_icon={<ChevronLeft className="w-[18px] h-[18px] lg:w-5 lg:h-5" />}
            color_type="tertiary"
            className="whitespace-nowrap"
          />
          : ""}
      </div>

      {/* attributes */}
      <ul className="flex items-center lg:hidden gap-3 px-1 py-3 lg:pb-6 overflow-x-auto hide-scrollbar">
        {attributes?.map((label) => renderAttributeItem(label))}
      </ul>

      <ul className="hidden lg:flex lg:flex-wrap items-center flex-wrap gap-3 px-1 py-3 pb-6 overflow-x-auto hide-scrollbar">
        {attributes?.map((label) => renderAttributeItem(label))}
      </ul>


      {/* Size Guide Modal */}
      {isSize && (
        <ModalComponent
          isOpen={isSizeModalOpen}
          onClose={() => setIsSizeModalOpen(false)}
          title="راهنمای اندازه"
          size="lg"
          full={true}
        >
          <div className="overflow-y-auto max-h-[520px]">
            <div
              className="text-editor-single-products"
              dangerouslySetInnerHTML={{ __html: sizeHintDescription || "" }}
            />
          </div>
        </ModalComponent>
      )}

      {/* All Options Modal*/}
      {hasOptionsModal && (
        <ModalComponent
          isOpen={isOptionsModalOpen}
          onClose={() => setIsOptionsModalOpen(false)}
          title={`انتخاب ${title}`}
          description={`دارای ${attributes.length} ${title}`}
          partialHeight={300}>

          <div className="space-y-1">
            <ul className="grid grid-cols-2 gap-3">
              {attributes?.map((label) => renderAttributeItem(label))}
            </ul>
          </div>
        </ModalComponent>
      )}
    </div>
  );
}